import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import articleBg from '@/assets/article-bg.jpg';

interface RefundSection {
  id: string;
  title: string;
  content: string;
}

const refundPolicySections: RefundSection[] = [
  {
    id: "nature-of-services",
    title: "1. Nature of Our Services",
    content: `Animind provides digital, cloud-based, AI-assisted creative production tools and related services. Our services may include, without limitation, AI video generation, AI image generation, animation creation, character consistency tools, scene design tools, shot-level refinement tools, project workflow features, business account access, usage credits, points, and other digital production functions.

Because Animind provides digital services that may be accessed, consumed, or delivered immediately after payment, all payments are subject to the refund limitations described in this policy.`
  },
  {
    id: "business-to-business",
    title: "2. Business-to-Business Service",
    content: `Animind is intended for business users only. Access to the platform may require business verification, including submission of company information, business license, registration documents, or other verification materials.

Payments made by approved business users are treated as commercial business transactions. You are responsible for ensuring that your purchase is suitable for your business needs before completing payment.`
  },
  {
    id: "general-refund-rule",
    title: "3. General Refund Rule",
    content: `Unless otherwise required by applicable law or expressly agreed in writing by ATELIER TENET INC., all completed purchases are final and non-refundable once the relevant digital service, account access, membership, points, credits, generation capacity, or professional service has been activated, delivered, used, or made available.

This includes, but is not limited to:
Paid memberships or account access fees.
Points, credits, tokens, generation balance, or usage packages.
AI generation services that have been initiated, consumed, or completed.
Project setup fees, onboarding fees, integration fees, or professional service fees.
Custom production support, enterprise configuration, consulting, or technical service fees.`
  },
  {
    id: "refunds-may-be-considered",
    title: "4. Refunds May Be Considered",
    content: `We may consider a refund request in the following limited circumstances:

You were charged more than once for the same transaction due to a technical or payment processing error.

Payment was completed but your account was not activated due to an issue caused by Animind.

You purchased credits, points, or usage balance, but none of them were used, consumed, transferred, expired, or applied to any service.

A service was materially unavailable due to a verified technical failure on Animind's side, and we were unable to provide a reasonable remedy, credit, replacement access, or extension.

A refund is required by applicable law.

All refund requests are reviewed at the sole discretion of ATELIER TENET INC., unless otherwise required by law.`
  },
  {
    id: "non-refundable-situations",
    title: "5. Non-Refundable Situations",
    content: `Refunds will generally not be issued in the following situations:

You changed your mind after purchase.

You no longer need the service.

You purchased the wrong plan, package, or amount of credits.

You failed to use the service during the applicable period.

You are dissatisfied with an AI-generated result where the service was properly delivered and the generation function was available.

You used, consumed, or partially consumed purchased credits, points, tokens, or generation balance.

Your account was suspended or terminated due to violation of our Terms of Service, misuse, fraud, abuse, illegal activity, infringement, unauthorized resale, or other prohibited conduct.

Your business verification documents were false, incomplete, misleading, or unverifiable.

You failed to provide required information for onboarding, verification, compliance, billing, tax, or payment review.

You experienced an issue caused by your own device, browser, internet connection, third-party software, unsupported file format, or incorrect usage of the platform.

You initiated a chargeback or payment dispute before contacting us and allowing us a reasonable opportunity to resolve the issue.`
  },
  {
    id: "credits-points-balance",
    title: "6. Credits, Points, and Usage Balance",
    content: `If Animind offers prepaid credits, points, tokens, or usage balance, such balance is a digital service entitlement and not a stored-value financial product, bank deposit, gift card, or cash equivalent.

Credits, points, or usage balance have no cash value, are not transferable unless expressly permitted by us, and cannot be redeemed for cash.

Unused credits, points, or usage balance may be subject to expiration, account limitations, plan restrictions, or other conditions stated at the time of purchase or in our Terms of Service.

Once credits, points, tokens, or usage balance have been used, consumed, applied, or deducted, they are non-refundable.`
  },
  {
    id: "memberships-recurring",
    title: "7. Memberships and Recurring Billing",
    content: `If Animind offers recurring membership plans or subscriptions, you may cancel future renewals by following the cancellation instructions in your account or by contacting support@animind.net.

Cancellation prevents future billing but does not automatically entitle you to a refund for the current billing period, unless required by law or expressly approved by us.

Your access may continue until the end of the paid billing period, unless your account is suspended or terminated under our Terms of Service.`
  },
  {
    id: "how-to-request",
    title: "8. How to Request a Refund",
    content: `To request a refund, contact us at:
Email: support@animind.net

Your request should include:
Your full name.
Company name.
Account email address.
Transaction date.
Transaction amount.
Order ID, invoice number, or payment reference.
Reason for the refund request.
Relevant screenshots, error messages, or supporting materials.

Refund requests should be submitted within 7 days of the transaction date unless a longer period is required by applicable law.`
  },
  {
    id: "review-process",
    title: "9. Review Process",
    content: `After receiving your refund request, we may review account activity, usage logs, transaction records, generation history, technical logs, communication records, and any relevant supporting information.

We may ask you to provide additional information before making a decision.

Approval of a refund in one case does not create an obligation to provide refunds in other cases.`
  },
  {
    id: "refund-method",
    title: "10. Refund Method and Processing Time",
    content: `Approved refunds will generally be issued to the original payment method used for the purchase.

Refund processing times may vary depending on the payment processor, card network, bank, or financial institution. Once we approve and submit a refund, it may take several business days for the refunded amount to appear in your account.

We are not responsible for delays caused by banks, card issuers, payment processors, or other financial institutions.`
  },
  {
    id: "partial-refunds",
    title: "11. Partial Refunds and Service Credits",
    content: `In some cases, instead of a monetary refund, we may offer a partial refund, service credit, replacement credits, account extension, or other reasonable remedy.

Any service credit provided by Animind has no cash value and may be subject to expiration, usage limitations, or account restrictions.`
  },
  {
    id: "chargebacks",
    title: "12. Chargebacks and Payment Disputes",
    content: `Before initiating a chargeback or payment dispute with your bank, card issuer, or payment provider, you should contact us at support@animind.net so that we can review and attempt to resolve the issue.

If you initiate a chargeback or payment dispute, we may suspend your account, restrict access to paid services, freeze remaining credits or usage balance, and provide relevant transaction records, usage logs, account records, and communications to the payment processor or financial institution.

Fraudulent, abusive, or bad-faith chargebacks may result in permanent account termination and may affect future access to Animind services.`
  },
  {
    id: "fraud-abuse",
    title: "13. Fraud, Abuse, and Compliance Review",
    content: `We reserve the right to deny refunds, suspend accounts, cancel transactions, or restrict services if we suspect fraud, abuse, unauthorized payment activity, sanctions risk, money laundering risk, identity misrepresentation, document falsification, unauthorized resale, infringement, or violation of our Terms of Service.

We may also delay or decline refunds where required for legal, regulatory, tax, accounting, compliance, payment network, or fraud-prevention reasons.`
  },
  {
    id: "changes",
    title: "14. Changes to This Refund Policy",
    content: `We may update this Refund Policy from time to time. The updated version will be posted on this page with a new effective date.

Your continued use of Animind after the updated Refund Policy becomes effective means that you acknowledge and agree to the updated policy.`
  },
  {
    id: "contact",
    title: "15. Contact Us",
    content: `For refund, billing, or payment questions, contact:

ATELIER TENET INC.
3660 Midland Ave, Suite 326
Scarborough, ON M1V 0B8
Canada
Email: support@animind.net
Website: https://www.animind.net`
  }
];

const refundPolicyMeta = {
  effectiveDate: "[Month Day, Year]",
  website: "https://www.animind.net",
  company: "ATELIER TENET INC.",
  address: "3660 Midland Ave, Suite 326, Scarborough, ON M1V 0B8, Canada",
  contact: "support@animind.net"
};

const RefundPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div 
      className="min-h-screen bg-black text-gray-300 bg-cover bg-center bg-no-repeat bg-fixed"
      style={{ backgroundImage: `url(${articleBg})` }}
    >
      <div className="min-h-screen bg-black/80">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </motion.div>

          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Refund Policy
            </h1>
            <div className="text-gray-400 space-y-1">
              <p>Effective Date: {refundPolicyMeta.effectiveDate}</p>
              <p>Website: {refundPolicyMeta.website}</p>
              <p>Company: {refundPolicyMeta.company}</p>
              <p>Address: {refundPolicyMeta.address}</p>
              <p>Contact: {refundPolicyMeta.contact}</p>
            </div>
          </motion.header>

          <div className="lg:grid lg:grid-cols-4 lg:gap-12">
            <motion.nav
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block lg:col-span-1"
            >
              <div className="sticky top-8">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Table of Contents
                </h2>
                <ul className="space-y-2">
                  {refundPolicySections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className="text-sm text-gray-400 hover:text-orange-500 transition-colors duration-200 text-left"
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.nav>

            <motion.main
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-3"
            >
              <p className="text-gray-400 mb-8 leading-relaxed">
                This Refund Policy explains how refunds, credits, cancellations, and billing issues are handled for Animind, a business-to-business AI film and animation creation platform operated by ATELIER TENET INC.
              </p>
              <p className="text-gray-400 mb-12 leading-relaxed">
                By purchasing access, credits, points, membership plans, professional services, or any other paid services through Animind, you agree to this Refund Policy.
              </p>

              {refundPolicySections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="mb-10 scroll-mt-8"
                >
                  <h2 className="text-xl font-semibold text-white mb-4">
                    {section.title}
                  </h2>
                  <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </section>
              ))}
            </motion.main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;